import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const RETRY_DELAY_MS = 500;
const POST_SIGNIN_GRACE_MS = 1500;
const AUTH_EVENT_GRACE_WINDOW_MS = 3000;

/**
 * useAdminCheck — resolves whether the current user has the `admin` role.
 *
 * P3 OPTIMIZATION: backed by react-query so a SINGLE has_role RPC is shared
 * across every consumer (SubscriptionRoute, useEffectiveAccess, etc.) and
 * survives route navigation. Previously each new mount triggered a fresh RPC
 * and forced a "Verificando suscripción…" spinner on every navigation.
 *
 * PRESERVED security guarantees:
 *  - Grace/retry logic (3 attempts) is kept inside the queryFn for the
 *    JWT-race window after SIGNED_IN / TOKEN_REFRESHED.
 *  - `isAdmin` is NEVER degraded true → false within a session. A module-level
 *    sticky map remembers the last confirmed `true` per user id; if the cache
 *    is invalidated and a transient `false` arrives, we keep returning `true`.
 *    Only SIGNED_OUT or a user id change clears the sticky.
 *  - Invalidation runs on SIGNED_IN / TOKEN_REFRESHED / SIGNED_OUT via
 *    queryClient.invalidateQueries.
 */

// Module-level: timestamp of the last SIGNED_IN/TOKEN_REFRESHED event.
let lastAuthEventAt = 0;

// Module-level sticky admin store. Once a user is confirmed admin, we never
// degrade to false until SIGNED_OUT or user id changes.
const stickyAdminByUserId = new Map<string, true>();

export const adminCheckQueryKey = (userId: string | undefined) =>
  ["admin-check", userId ?? "anon"] as const;

async function runHasRole(userId: string, attempt: number) {
  const { data: sessionData } = await supabase.auth.getSession();
  const hasSession = !!sessionData?.session?.access_token;

  const { data, error } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });

  console.log("[useAdminCheck] has_role response", {
    userId,
    attempt,
    sessionStatus: hasSession ? "present" : "absent",
    data,
    error: error ? { message: error.message, code: (error as any).code ?? null } : null,
    outcome: error ? "error" : data === true ? "true" : "false",
  });

  return { data, error, hasSession };
}

async function resolveAdminWithGrace(userId: string): Promise<boolean> {
  // ── Attempt 1 ──
  const first = await runHasRole(userId, 1);
  if (!first.error && first.data === true) return true;
  if (!first.hasSession) return false;

  // ── FAST PATH ── no recent auth event → no JWT-race risk
  const sinceLastAuth = Date.now() - lastAuthEventAt;
  const withinGrace = lastAuthEventAt > 0 && sinceLastAuth < AUTH_EVENT_GRACE_WINDOW_MS;
  if (!first.error && first.data !== true && !withinGrace) {
    console.log("[useAdminCheck] Fast-path non-admin (no recent auth event)", {
      sinceLastAuthMs: sinceLastAuth,
    });
    return false;
  }

  // ── SLOW/GRACE PATH (attempt 2) ──
  await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
  const second = await runHasRole(userId, 2);
  if (!second.error && second.data === true) {
    console.log("[useAdminCheck] Recovered admin=true on retry");
    return true;
  }
  if (!second.hasSession) return false;

  // ── Attempt 3 (post-grace) ──
  await new Promise((r) => setTimeout(r, POST_SIGNIN_GRACE_MS));
  const third = await runHasRole(userId, 3);
  if (!third.error && third.data === true) {
    console.log("[useAdminCheck] Recovered admin=true on third attempt (post-grace)");
    return true;
  }

  console.log("[useAdminCheck] Confirmed non-admin after grace-window attempts");
  return false;
}

export const useAdminCheck = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | undefined>(undefined);

  // Drop sticky entry whenever the user id changes (login as different user
  // or logout). This prevents admin status from "leaking" between users.
  useEffect(() => {
    if (prevUserIdRef.current && prevUserIdRef.current !== user?.id) {
      stickyAdminByUserId.delete(prevUserIdRef.current);
      // Also drop the stale query for the previous user.
      queryClient.removeQueries({ queryKey: adminCheckQueryKey(prevUserIdRef.current) });
    }
    prevUserIdRef.current = user?.id;
  }, [user?.id, queryClient]);

  // Auth state listener — invalidate on SIGNED_IN / TOKEN_REFRESHED / SIGNED_OUT.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN" || event === "USER_UPDATED") {
        lastAuthEventAt = Date.now();
        const uid = session?.user?.id;
        // Only invalidate if we don't already have a confirmed true (sticky).
        if (uid && !stickyAdminByUserId.has(uid)) {
          console.log("[useAdminCheck] auth event → invalidate:", event);
          queryClient.invalidateQueries({ queryKey: adminCheckQueryKey(uid) });
        }
      } else if (event === "SIGNED_OUT") {
        lastAuthEventAt = 0;
        // Clear ALL sticky entries on sign-out.
        stickyAdminByUserId.clear();
        queryClient.removeQueries({ queryKey: ["admin-check"] });
      }
    });
    return () => { sub.subscription.unsubscribe(); };
  }, [queryClient]);

  const query = useQuery({
    queryKey: adminCheckQueryKey(user?.id),
    queryFn: async () => {
      if (!user?.id) return false;
      const result = await resolveAdminWithGrace(user.id);
      if (result === true) {
        stickyAdminByUserId.set(user.id, true);
      }
      return result;
    },
    enabled: !!user?.id,
    // Cache for the whole session — invalidated explicitly on auth events.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // STICKY TRUE GUARANTEE: never degrade an already-confirmed admin to false
  // for this user, regardless of transient query results.
  const stickyTrue = user?.id ? stickyAdminByUserId.has(user.id) : false;
  const isAdmin = stickyTrue || query.data === true;

  // Loading is only true on the very first resolution for this user.
  // If sticky says true or we already have any data, loading=false.
  const loading = !!user && !stickyTrue && query.data === undefined && (query.isLoading || query.isFetching);

  return { isAdmin, loading };
};
